'use client'
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/hooks/use-auth";
import { Role } from "@prisma/client";
import { BarChart, ListTodo, Settings, Users } from "lucide-react";
import Link from "next/link";

export default function Home() {
  const { user, isLoading } = useAuth()

  return (
    <div className="container py-5 px-4">
      <h1 className="text-3xl font-bold mb-6">Dashboard</h1>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="h-5 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-gray-200 rounded w-1/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <>
          {!user ? (
            <Card>
              <CardHeader>
                <CardTitle>Welcome to Alec016 Wikis</CardTitle>
                <CardDescription>Sign in to access all available features.</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild>
                  <Link href="/docs">View Public Docs</Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Total Tasks</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">12</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">In Progress</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">4</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Completed</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">7</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Pending Review</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">1</div>
                  </CardContent>
                </Card>
              </div>

              <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <ListTodo className="h-5 w-5" />
                      <CardTitle>Manage Tasks</CardTitle>
                    </div>
                    <CardDescription>View and manage your tasks</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button asChild>
                      <Link href="/tasks">Go to Tasks</Link>
                    </Button>
                  </CardContent>
                </Card>

                {(user.role === Role.Editor ||
                  user.role === Role.Admin ||
                  user.role === Role.SuperAdmin) && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <BarChart className="h-5 w-5" />
                        <CardTitle>Analytics</CardTitle>
                      </div>
                      <CardDescription>View task analytics and reports</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button asChild>
                        <Link href="/analytics">View Analytics</Link>
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {(user.role === Role.Admin || user.role === Role.SuperAdmin) && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        <CardTitle>Manage Users</CardTitle>
                      </div>
                      <CardDescription>View and manage system users</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button asChild>
                        <Link href="/users">Manage Users</Link>
                      </Button>
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Settings className="h-5 w-5" />
                      <CardTitle>Settings</CardTitle>
                    </div>
                    <CardDescription>Manage your account settings</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button asChild>
                      <Link href="/settings">Go to Settings</Link>
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}
